import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Mail, Lock, Eye, EyeOff, Sparkles } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import CinematicNav from "@/components/CinematicNav";

/**
 * Login cinematic — coerente con landing TotalKlick.
 * Glass form scuro, particle starfield background, micro-glow.
 */
export default function Login() {
    const { login } = useAuth();
    const nav = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPwd, setShowPwd] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const canvasRef = useRef(null);

    // Vanilla starfield (no R3F per ricordo handoff)
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        let raf = 0;
        const resize = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
        };
        resize();
        window.addEventListener("resize", resize);
        const stars = Array.from({ length: 180 }, () => ({
            x: Math.random() * window.innerWidth,
            y: Math.random() * window.innerHeight,
            r: Math.random() * 1.2 + 0.3,
            s: Math.random() * 0.05 + 0.02,
            o: Math.random() * 0.6 + 0.15,
        }));
        const loop = () => {
            ctx.fillStyle = "#05050F";
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            stars.forEach((st) => {
                ctx.fillStyle = `rgba(180,200,255,${st.o})`;
                ctx.beginPath();
                ctx.arc(st.x, st.y, st.r, 0, Math.PI * 2);
                ctx.fill();
                st.y += st.s;
                if (st.y > canvas.height) st.y = 0;
            });
            raf = requestAnimationFrame(loop);
        };
        loop();
        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener("resize", resize);
        };
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            const user = await login(email, password);
            const params = new URLSearchParams(window.location.search);
            const next = params.get("next");
            if (next && next.startsWith("/")) {
                // /api/* è un endpoint backend (es. SSO authorize), serve full reload
                if (next.startsWith("/api/")) window.location.href = next;
                else nav(next);
            }
            else if (user.role === "superadmin") nav("/superadmin");
            else nav("/app");
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            className="relative min-h-screen w-full overflow-hidden"
            style={{ background: "#05050F", color: "#fff", fontFamily: "Geist, Outfit, sans-serif" }}
            data-testid="login-page"
        >
            <canvas ref={canvasRef} className="absolute inset-0 pointer-events-none" style={{ zIndex: 0 }} />
            {/* Radial blue glow */}
            <div
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[700px] pointer-events-none"
                style={{ background: "radial-gradient(ellipse, rgba(79,139,255,0.22) 0%, transparent 65%)", filter: "blur(40px)", zIndex: 0 }}
            />

            <CinematicNav />

            <div className="relative z-10 min-h-screen flex items-center justify-center px-5 pt-24 pb-12">
                <div className="w-full max-w-md">
                    {/* Header */}
                    <div className="text-center mb-8">
                        <div
                            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-6"
                            style={{ background: "rgba(79,139,255,0.08)", border: "1px solid rgba(79,139,255,0.22)" }}
                        >
                            <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#4F8BFF", boxShadow: "0 0 8px #4F8BFF" }} />
                            <span className="text-[10px] uppercase tracking-[0.22em] text-[#4F8BFF]" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                                accesso ecosistema
                            </span>
                        </div>
                        <h1
                            className="text-white tracking-tight"
                            style={{
                                fontFamily: "Geist, Outfit, sans-serif",
                                fontSize: "clamp(2rem, 4.4vw, 3rem)",
                                lineHeight: 1.05,
                                letterSpacing: "-0.03em",
                                fontWeight: 200,
                            }}
                        >
                            Bentornato nell&apos;<span style={{ fontWeight: 600 }}>Ecosistema</span>.
                        </h1>
                        <p className="mt-4 text-white/55 text-[14px]" style={{ fontFamily: "Outfit, sans-serif" }}>
                            Un account. Tutti i moduli. Anche quelli su dominio dedicato.
                        </p>
                    </div>

                    {/* Glass card */}
                    <div
                        className="rounded-2xl p-7 md:p-8"
                        style={{
                            background: "rgba(15,18,36,0.65)",
                            border: "1px solid rgba(255,255,255,0.08)",
                            backdropFilter: "blur(24px) saturate(140%)",
                            WebkitBackdropFilter: "blur(24px) saturate(140%)",
                            boxShadow: "0 30px 80px -20px rgba(0,0,0,0.6)",
                        }}
                    >
                        <form onSubmit={submit} className="space-y-4">
                            <Field label="Email" icon={Mail}>
                                <input
                                    data-testid="login-email"
                                    type="email"
                                    required
                                    autoComplete="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="tu@esempio.it"
                                    className="cine-input pl-11"
                                />
                            </Field>

                            <Field label="Password" icon={Lock}>
                                <input
                                    data-testid="login-password"
                                    type={showPwd ? "text" : "password"}
                                    required
                                    autoComplete="current-password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="••••••••••••"
                                    className="cine-input pl-11 pr-11"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPwd((v) => !v)}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/80"
                                    data-testid="login-toggle-pwd"
                                    aria-label="Mostra password"
                                >
                                    {showPwd ? <EyeOff size={15} /> : <Eye size={15} />}
                                </button>
                            </Field>

                            {error && (
                                <div
                                    data-testid="login-error"
                                    className="px-3 py-2.5 rounded-lg text-[13px]"
                                    style={{ background: "rgba(244,63,94,0.1)", border: "1px solid rgba(244,63,94,0.3)", color: "#FCA5A5" }}
                                >
                                    {error}
                                </div>
                            )}

                            <button
                                data-testid="login-submit"
                                type="submit"
                                disabled={loading}
                                className="w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-full text-white text-[14px] transition hover:scale-[1.02] disabled:opacity-50 disabled:hover:scale-100"
                                style={{
                                    background: "linear-gradient(135deg, #4F8BFF 0%, #2B5AFF 100%)",
                                    fontFamily: "Geist, sans-serif",
                                    letterSpacing: "0.04em",
                                    boxShadow: "0 18px 60px -12px rgba(79,139,255,0.6)",
                                }}
                            >
                                {loading ? "Accesso in corso…" : <>Apri Workspace <ArrowRight size={15} /></>}
                            </button>

                            <div className="flex items-center justify-between pt-1.5 text-[12.5px]">
                                <Link to="/forgot-password" className="text-white/50 hover:text-white transition" data-testid="link-forgot">
                                    Password dimenticata?
                                </Link>
                                <Link to="/signup" data-testid="link-signup" className="text-[#4F8BFF] hover:text-white transition font-medium">
                                    Crea un account →
                                </Link>
                            </div>
                        </form>
                    </div>

                    {/* SSO hint */}
                    <p className="text-center text-white/35 text-[11.5px] mt-6" style={{ fontFamily: "Outfit, sans-serif" }}>
                        Lo stesso account dà accesso a TotalPL · TotalKlickADS · Design · Aria · OfferteInCittà · EllyMenu · GoStreet · PhotoVendo
                    </p>
                </div>
            </div>

            <style>{`
                .cine-input {
                    width: 100%;
                    padding: 13px 14px;
                    border-radius: 12px;
                    border: 1px solid rgba(255,255,255,0.1);
                    background: rgba(255,255,255,0.03);
                    color: #fff;
                    font-family: 'Geist', 'Outfit', sans-serif;
                    font-size: 14px;
                    transition: all 0.2s;
                }
                .cine-input:focus {
                    outline: none;
                    border-color: rgba(79,139,255,0.55);
                    background: rgba(255,255,255,0.05);
                    box-shadow: 0 0 0 3px rgba(79,139,255,0.12);
                }
                .cine-input::placeholder { color: rgba(255,255,255,0.3); }
                input.cine-input.pl-11, textarea.cine-input.pl-11 { padding-left: 44px; }
                input.cine-input.pr-11, textarea.cine-input.pr-11 { padding-right: 44px; }
            `}</style>
        </div>
    );
}

function Field({ label, icon: Icon, children }) {
    return (
        <div>
            <label
                className="block text-[10.5px] uppercase tracking-[0.18em] font-bold mb-1.5 text-white/45"
                style={{ fontFamily: "JetBrains Mono, monospace" }}
            >
                {label}
            </label>
            <div className="relative">
                {Icon && <Icon size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />}
                {children}
            </div>
        </div>
    );
}
